/** Shared UI pieces in the Strats language. */
import { Component, type ReactNode } from "react";
import type { Bot } from "../lib/types.ts";

export const ICONS = {
  today: "M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  needs: "M5 21V4h11l-2 4 2 4H5", chats: "M4 5h16v11H9l-5 4z", portfolio: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z",
  plus: "M12 5v14M5 12h14", library: "M4 5h6v14H4zM14 5h6v14h-6z", backtests: "M12 7v5l3 2M4 12a8 8 0 1 0 2.3-5.7M4 4v4h4",
  trades: "M4 6h16M4 12h16M4 18h10", market: "M3 17l6-6 4 4 8-8", settings: "M4 7h10M18 7h2M4 17h4M12 17h8M16 5a2 2 0 1 0 0 4a2 2 0 1 0 0-4M10 15a2 2 0 1 0 0 4a2 2 0 1 0 0-4",
  pause: "M9 5v14M15 5v14", play: "M7 5l12 7-12 7z", back: "M15 18l-6-6 6-6", you: "M12 12a4 4 0 1 0 0-8a4 4 0 1 0 0 8M4 21a8 8 0 0 1 16 0",
  bots: "M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18M3 12h18",
} as const;

export function Icon({ d, size = 18 }: { d: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}

export function Page({ eyebrow, title, actions, children, width = 880 }: { eyebrow?: ReactNode; title?: ReactNode; actions?: ReactNode; children: ReactNode; width?: number }) {
  return (
    <main className="px-4 pb-24 pt-6 md:px-8 md:pb-16 md:pt-10">
      <div className="mx-auto flex flex-col gap-8" style={{ maxWidth: width }}>
        {(eyebrow || title || actions) && (
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>{eyebrow && <div className="eyebrow">{eyebrow}</div>}{title && <h1 className="h1 mt-1.5">{title}</h1>}</div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </main>
  );
}

export function Stat({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: "up" | "down"; hint?: string }) {
  return (
    <div className="tile" title={hint}>
      <div className="text-[12px] text-muted">{label}</div>
      <div className={`num text-[18px] ${tone === "up" ? "text-accent-ink" : tone === "down" ? "text-warn-ink" : "text-ink-strong"}`}>{value}</div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="card flex flex-col items-center gap-2 px-6 py-10 text-center"><div className="font-semibold text-ink-strong">{title}</div>{children && <div className="max-w-md text-muted">{children}</div>}</div>;
}

export function ErrorNote({ error }: { error: Error | null | undefined }) {
  return error ? <div role="alert" className="rounded-lg bg-warn-soft px-3 py-2 text-warn-ink">{error.message}</div> : null;
}

export function Loading() { return <div className="py-10 text-center text-muted" aria-live="polite">Loading…</div>; }

export type Health = "ok" | "risk" | "stalled" | "paused" | "draft";
export function botHealth(b: Bot, now = Date.now()): Health {
  if (b.status === "error") return "stalled";
  if (b.status === "halted") return "risk";
  if (b.status === "paused") return "paused";
  if (!b.last_equity_at) return "draft";
  if (now - b.last_equity_at > 3 * 3_600_000 + 10 * 60_000) return "stalled";
  return "ok";
}
const HEALTH: Record<Health, [string, string]> = {
  ok: ["bg-ok", "on track"], risk: ["bg-warn", "safety step fired"], stalled: ["bg-danger", "stalled"], paused: ["bg-line-strong", "paused"], draft: ["bg-line-strong", "starting"],
};
export function HealthDot({ h }: { h: Health }) {
  const [cls, label] = HEALTH[h];
  return <span role="img" aria-label={label} title={label} className={`inline-block h-2 w-2 shrink-0 rounded-full ${cls}`} />;
}

/** Bot status as plain coloured text (no pills). */
export function StatusChip({ status, mode, exchange }: { status: Bot["status"]; mode: Bot["mode"]; exchange?: Bot["exchange"] }) {
  const tone = status === "active" ? "text-ok-ink" : status === "paused" ? "text-muted" : "text-warn-ink";
  const word = { active: "Running", paused: "Paused", halted: "Stopped by safety", error: "Needs attention", closed: "Closed" }[status];
  const where = exchange === "binance-demo" ? "Binance Demo" : exchange === "paper-parity" ? "Paper · exact fills" : mode === "paper" ? "Paper" : "Live";
  return <span className={`text-[13px] font-medium ${tone}`}>{word} · {where}</span>;
}

export function Tabs<T extends string>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto border-b border-line">
      {options.map(([v, l]) => (
        <button key={v} role="tab" aria-selected={value === v} onClick={() => onChange(v)}
          className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[14px] ${value === v ? "border-ink-strong font-semibold text-ink-strong" : "border-transparent text-muted hover:text-ink"}`}>{l}</button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-lg bg-soft p-0.5">
      {options.map(([v, l]) => (
        <button key={v} role="radio" aria-checked={value === v} onClick={() => onChange(v)}
          className={`rounded-md px-2.5 py-1 text-[13px] ${value === v ? "bg-card font-medium text-ink-strong shadow-sm" : "text-muted"}`}>{l}</button>
      ))}
    </div>
  );
}

/** Keeps one broken panel from blanking the whole app. */
export class Boundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return <div role="alert" className="m-6 rounded-lg bg-warn-soft px-4 py-3 text-warn-ink">Something on this page failed to draw: {this.state.error.message} <button className="ml-2 underline" onClick={() => this.setState({ error: null })}>Try again</button></div>;
  }
}
